import { Recipe } from "@juicy/models";
import { BatchDto, FreeformIngredientDto, IngredientDto } from "@juicy/models/dto";
import { useEffect, useState } from "react";
import { useToast } from "react-native-toast-notifications";
import { useRecoilState } from "recoil";
import { apiService } from "../../App";
import { IngredientFormValuesAtom } from "../batch/batchAtoms";
import { DataListType, useDataList } from "../common/dataList/useDataList";
import { MixerIngredientFormValues } from "./mixer-flavor/MixeIngredientForm";

const multipliers = {
    nicBase: 1.0333,
    vg: 1.2616195495927167,
    pg: 1.0361663652802893,
    flavors: 1.048148148148148,
};

const randomString = () => Math.random().toString(36).substring(2, 12);
export interface Result {
    nicBase: number;
    flavors: MixerFlavor[];
    vg: number;
    pg: number;
    sum: number;
}
export interface MixerResult {
    g: Result;
    ml: Result;
}

export interface MixerFlavor {
    name: string;
    value: number;
}

export const useMixer = (recipeId?: string) => {
    const createEmptyMixerFormValuesElement = (): MixerIngredientFormValues => {
        return {
            _id: randomString(),
            percentage: 0,
        };
    };

    const [baseNicStrength, setBaseNicStrength] = useState(100);
    const [baseVg, setBaseVg] = useState(0);
    const [targetNicStrength, setTargetNicStrength] = useState(6);
    const [targetVg, setTargetVg] = useState(70);
    const [batchSize, setBatchSize] = useState(10);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedRecipeId, setSelectedRecipeId] = useState<number | undefined>();

    const toast = useToast();

    const [result, setResult] = useState<MixerResult | null>(null);

    const [ingredientFormValues, setIngredientFormValues] = useRecoilState(IngredientFormValuesAtom);
    const { data, refresh } = useDataList(DataListType.Recipe);

    useEffect(() => {
        if (recipeId) {
            const list: Recipe[] = data.list;
            if (list.length > 0) {
                const keys = recipeId.split("-").map((x) => parseInt(x, 10));
                const recipe = list.find((r) => r.userId === keys[0] && r.id === keys[1] && r.version === keys[2]);
                setSelectedRecipeId(recipe?.id);
                if (recipe) {
                    setIngredientFormValues(
                        recipe.ingredients.map((i) => ({
                            _id: randomString(),
                            percentage: i.percentage,
                            flavor: {
                                value: i.flavor!.id.toString(),
                                label: i.flavor!.manufacturer.shortname + " " + i.flavor!.name,
                                selected: false,
                            },
                        }))
                    );
                }
            } else {
                void refresh();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipeId, data.list, refresh]);

    useEffect(() => {
        setIngredientFormValues([createEmptyMixerFormValuesElement()]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onIngredientChange = (values: MixerIngredientFormValues) => {
        const index = ingredientFormValues.findIndex((i) => i._id === values._id);
        const newIngredientFormValues = [...ingredientFormValues];
        newIngredientFormValues[index] = values;
        setIngredientFormValues(newIngredientFormValues);
    };

    const onAddIngredient = () => {
        setIngredientFormValues([...ingredientFormValues, createEmptyMixerFormValuesElement()]);
    };

    const onRemoveIngredient = (_id: string) => {
        setIngredientFormValues(ingredientFormValues.filter((i) => i._id !== _id));
    };

    useEffect(() => {
        const basePg = 100 - baseVg;
        const targetPg = 100 - targetVg;
        let remainingBatchSize = batchSize;
        console.log({ targetPg });
        const basePerBatchSize = targetNicStrength / baseNicStrength;
        console.log({ basePerBatchSize });
        const baseAmount = basePerBatchSize * batchSize;
        console.log({ baseAmount });
        const baseNicPercentage = (baseNicStrength / 1000) * 100;
        console.log({ baseNicPercentage });
        const restPercentage = 100 - baseNicPercentage;
        console.log({ restPercentage });
        const baseRest = baseAmount * (restPercentage / 100);
        console.log({ baseRest });
        const baseNic = baseAmount * (baseNicPercentage / 100);
        console.log({ baseNic });
        const baseRestVg = baseRest * (baseVg / 100);
        console.log({ baseRestVg });
        const baseRestPg = baseRest * (basePg / 100);
        console.log({ baseRestPg });
        remainingBatchSize -= baseNic;
        console.log({ remainingBatchSize });
        const remainingVg = remainingBatchSize * (targetVg / 100) - baseRestVg;
        console.log({ remainingVg });
        let remainingPg = remainingBatchSize * (targetPg / 100) - baseRestPg;
        console.log({ remainingPg });

        const recipeFlavors = ingredientFormValues
            .filter((f) => !!f.flavor?.label && !!f.percentage && f.percentage > 0)
            .map<{ name: string; value: number }>((f) => {
                return { name: f.flavor?.label, value: batchSize * (f.percentage! / 100) } as any;
            });
        const totalFlavorsInMg = recipeFlavors.reduce((acc, f) => {
            acc += f.value;
            return acc;
        }, 0);
        remainingPg -= totalFlavorsInMg;

        const ml: Result = {
            nicBase: Math.round(baseAmount * 100) / 100,
            vg: Math.round(remainingVg * 100) / 100,
            pg: Math.round(remainingPg * 100) / 100,
            flavors: recipeFlavors,
            sum: 0,
        };

        const g: Result = {
            nicBase: ml.nicBase * multipliers.nicBase,
            vg: ml.vg * multipliers.vg,
            pg: ml.pg * multipliers.pg,
            flavors: ml.flavors.map((f) => ({ name: f.name, value: f.value * multipliers.flavors })),
            sum: 0,
        };

        g.sum = Math.round((g.nicBase + g.pg + g.vg + g.flavors.reduce((acc, v) => acc + v.value, 0)) * 100) / 100;

        ml.sum = Math.round((ml.nicBase + ml.pg + ml.vg + ml.flavors.reduce((acc, f) => acc + f.value, 0)) * 100) / 100;

        setResult({
            ml,
            g,
        });
    }, [baseNicStrength, baseVg, targetNicStrength, targetVg, batchSize, ingredientFormValues]);

    const isValid =
        ingredientFormValues.length > 0 && ingredientFormValues.some((i) => !!i.flavor?.value && i.percentage > 0);

    const parseIngredients = (): IngredientDto[] => {
        return ingredientFormValues
            .filter((v) => v.flavor && v.flavor.value)
            .map((i) => {
                return {
                    flavorId: +i.flavor!.value,
                    percentage: i.percentage,
                };
            });
    };

    const saveBatch = async () => {
        if (!isValid) return;
        setIsSaving(true);
        const res = await apiService.batch.add({
            ingredients: parseIngredients(),
            nicStrength: targetNicStrength,
            vg: targetVg,
            size: batchSize,
            recipeId: selectedRecipeId,
        });
        console.log(res);
        toast.show("Batch created", { type: "success" });
        setIsSaving(false);
    };

    return {
        baseNicStrength,
        setBaseNicStrength,
        baseVg,
        setBaseVg,
        targetNicStrength,
        setTargetNicStrength,
        targetVg,
        setTargetVg,
        batchSize,
        setBatchSize,
        setResult,
        result,
        ingredientFormValues,
        onIngredientChange,
        onAddIngredient,
        onRemoveIngredient,
        saveBatch,
        isSaving,
        isValid,
    };
};
